import i18n from '@/i18n';
import { aiCopywritingTemplateAPI } from '@/utils/api';
import {
  BUILTIN_TITLE_PRESETS,
  inferTitlePresetCategory,
  mergeTitlePresets,
  normalizeTitlePresetScope,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';

/** 将后端 TemplateView 转为侧栏 / 模板库统一结构 */
export function mapCopywritingTemplateApiItem(item) {
  if (!item || typeof item !== 'object') return null;
  const content = String(item.content || '').trim();
  const name = String(item.title || item.name || '').trim() || '未命名模板';
  if (!content && !name) return null;
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  return {
    id,
    name,
    content: content || name,
    category: item.category || inferTitlePresetCategory({ name, content }),
    scope: normalizeTitlePresetScope(item.templateScope || item.scope || 'official'),
    platform: item.platform || '',
    forbiddenWords: Array.isArray(item.forbiddenWords) ? item.forbiddenWords : [],
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    source: 'api',
  };
}

export function mapBuiltinOfficialPresets() {
  return BUILTIN_TITLE_PRESETS.map((p) => ({
    id: String(p.id),
    name: p.name,
    content: p.content,
    category: p.category || inferTitlePresetCategory(p),
    scope: 'official',
    platform: 'shein',
    forbiddenWords: [],
    canEdit: false,
    canDelete: false,
    ownerUserId: null,
    source: 'builtin',
  }));
}

/**
 * 合并三 scope 列表（委托 titlePresetCatalog.mergeTitlePresets）。
 * 官方：接口成功 → 以返回为准（可为空）；接口失败 → 内置库兜底（FE-05）。
 */
export function mergeCopywritingTemplateScopes({
  personal = [],
  team = [],
  official = [],
  officialOk = false,
} = {}) {
  return mergeTitlePresets({ personal, team, official, officialOk });
}

/**
 * 分别请求 personal / team / official；
 * 官方仅在 list 失败时用内置库兜底（空列表且成功 → 不兜底，依赖 BE-05 种子）。
 */
export async function loadAiCopywritingTemplatesForStudio() {
  const scopes = ['personal', 'team', 'official'];
  const settled = await Promise.all(
    scopes.map(async (scope) => {
      try {
        const res = await aiCopywritingTemplateAPI.list({ scope });
        const items = (Array.isArray(res?.items) ? res.items : [])
          .map(mapCopywritingTemplateApiItem)
          .filter(Boolean)
          .map((row) => ({ ...row, scope }));
        return { scope, items, ok: true };
      } catch (_) {
        return { scope, items: [], ok: false };
      }
    }),
  );

  const byScope = Object.fromEntries(settled.map((row) => [row.scope, row]));
  return mergeCopywritingTemplateScopes({
    personal: byScope.personal?.items || [],
    team: byScope.team?.items || [],
    official: byScope.official?.items || [],
    officialOk: byScope.official?.ok === true,
  });
}

/**
 * 按 id 解析模板正文：优先新表 ai-copywriting-templates，再兼容旧 title-presets / 内置。
 */
export async function resolveCopywritingTemplateContentById(presetId, { titlePresetAPI } = {}) {
  const id = String(presetId || '').trim();
  if (!id) return '';

  if (/^\d+$/.test(id)) {
    try {
      const res = await aiCopywritingTemplateAPI.getById(id);
      const content = String(res?.template?.content || '').trim();
      if (content) return content;
    } catch {
      /* fallback */
    }
  }

  if (typeof titlePresetAPI?.getById === 'function') {
    try {
      const { preset } = await titlePresetAPI.getById(id);
      const content = String(preset?.content || '').trim();
      if (content) return content;
    } catch {
      /* fallback */
    }
  }

  const builtin = BUILTIN_TITLE_PRESETS.find((p) => String(p.id) === id);
  return builtin ? String(builtin.content || '').trim() : '';
}

/**
 * 团队模板创建人展示文案。
 * 自己 →「我」；能从团队花名册匹配 → 昵称；否则 →「团队成员」。
 */
export function resolveCopywritingTemplateOwnerLabel(
  preset,
  { currentUserId, ownerNameByUserId } = {},
) {
  if (!preset || preset.scope !== 'team') return '';
  const oid = preset.ownerUserId != null ? String(preset.ownerUserId) : '';
  if (!oid) return '';
  if (currentUserId != null && String(currentUserId) === oid) return i18n.t('workbench:ipeUtilError.scopeMe');
  const map = ownerNameByUserId && typeof ownerNameByUserId === 'object' ? ownerNameByUserId : null;
  const named = map ? (map[oid] || map[Number(oid)] || '') : '';
  const label = String(named || '').trim();
  return label || '团队成员';
}

/** 从 getTeamOverview.members 构建 userId → nickname 映射 */
export function buildTeamOwnerNameMap(members) {
  const map = {};
  if (!Array.isArray(members)) return map;
  for (const row of members) {
    const id = row?.userId != null ? String(row.userId) : '';
    if (!id) continue;
    const name = String(row?.nickname || '').trim();
    if (name) map[id] = name;
  }
  return map;
}

/** 下拉选项：按个人 / 团队 / 官方分组 */
export function groupTitlePresetsForSelect(presets = []) {
  const order = ['personal', 'team', 'official'];
  const labels = { personal: '个人', team: '团队', official: '官方' };
  const buckets = { personal: [], team: [], official: [] };
  (presets || []).forEach((p) => {
    const scope = normalizeTitlePresetScope(p?.scope);
    buckets[scope].push(p);
  });
  return order
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      scope: key,
      label: labels[key],
      presets: buckets[key],
    }));
}
